/**
 * PerformanceOverviewPanel — left column.
 *
 * TRUTH RULES:
 *   The brief forbids fabricating production statistics. This panel shows
 *   ONLY real, session-derived counters and clearly labels them DEV so no
 *   viewer confuses them with production analytics.
 *
 *   Counters (all derived from the live hook state):
 *     • Turns          — number of transcript items this session
 *     • Corrections    — total glossary normalizations applied
 *     • User turns     — subset of turns where role === 'user'
 *
 *   The mini sparkline is turns-per-minute rolling. No canned data.
 */

import { useMemo } from "react";
import { PanelShell } from "./PanelShell";

const RANGES = ["Session", "1H", "6H", "24H"];

function Sparkline({ points = [] }) {
    if (points.length < 2) {
        return (
            <svg width="100%" height="34" viewBox="0 0 200 34" preserveAspectRatio="none">
                <line x1="0" y1="30" x2="200" y2="30" stroke="rgba(0,229,255,0.15)" strokeWidth="1" />
            </svg>
        );
    }
    const max = Math.max(1, ...points);
    const path = points
        .map((v, i) => {
            const x = (i / (points.length - 1)) * 200;
            const y = 32 - (v / max) * 28;
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    const area = `${path} L200,34 L0,34 Z`;
    return (
        <svg width="100%" height="34" viewBox="0 0 200 34" preserveAspectRatio="none" aria-hidden="true">
            <path d={area} fill="rgba(0,229,255,0.10)" />
            <path d={path} fill="none" stroke="rgba(0,229,255,0.85)" strokeWidth="1.2" />
        </svg>
    );
}

function StatBlock({ label, value, sub, testId }) {
    return (
        <div className="flex flex-col gap-1" data-testid={testId}>
            <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                {label}
            </span>
            <span className="font-display text-[22px] leading-none font-light tracking-tight text-qoryn-text">
                {value}
            </span>
            {sub && (
                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan/80">
                    {sub}
                </span>
            )}
        </div>
    );
}

export function PerformanceOverviewPanel({ turns = [], diagnostics = [], sessionStartedAt }) {
    const stats = useMemo(() => {
        const turnCount = turns.length;
        const userTurns = turns.filter((t) => t.role === "user").length;
        const corrections = turns.reduce(
            (acc, t) => acc + (t.corrections?.length ?? 0),
            0,
        );
        return { turnCount, userTurns, corrections };
    }, [turns]);

    const sparkPoints = useMemo(() => {
        if (!sessionStartedAt || diagnostics.length === 0) return [];
        // Buckets: total session split into 24 slices, count response.generated per slice.
        const now = Date.now();
        const durMs = Math.max(1000, now - sessionStartedAt);
        const bucketMs = durMs / 24;
        const buckets = new Array(24).fill(0);
        for (const d of diagnostics) {
            if (d.type !== "response.generated" && d.type !== "user.sent_text") continue;
            const idx = Math.min(
                23,
                Math.floor((d.ts - sessionStartedAt) / bucketMs),
            );
            if (idx >= 0) buckets[idx]++;
        }
        return buckets;
    }, [diagnostics, sessionStartedAt]);

    return (
        <PanelShell
            title="Performance"
            testId="panel-performance"
            right={
                <span className="font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-textMuted/80 px-1.5 py-0.5 rounded border border-qoryn-textMuted/30">
                    DEV
                </span>
            }
        >
            <div className="flex items-center gap-1 mb-3">
                {RANGES.map((r) => (
                    <button
                        key={r}
                        disabled={r !== "Session"}
                        data-testid={`perf-range-${r.toLowerCase()}`}
                        className={`px-2 py-1 rounded font-mono-ui text-[9px] tracking-widest uppercase transition-colors duration-150 qoryn-focus ${
                            r === "Session"
                                ? "bg-qoryn-cyan/10 text-qoryn-cyan"
                                : "text-qoryn-textMuted/60 cursor-not-allowed"
                        }`}
                        title={r === "Session" ? "This session" : "Phase 1"}
                    >
                        {r}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
                <StatBlock
                    label="Turns"
                    value={stats.turnCount}
                    sub="live"
                    testId="perf-turns"
                />
                <StatBlock
                    label="User"
                    value={stats.userTurns}
                    sub="you"
                    testId="perf-user-turns"
                />
                <StatBlock
                    label="Fixes"
                    value={stats.corrections}
                    sub="asr"
                    testId="perf-corrections"
                />
            </div>
            <Sparkline points={sparkPoints} />
            <div className="mt-2 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                Session-derived · not production analytics
            </div>
        </PanelShell>
    );
}

export default PerformanceOverviewPanel;
